import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, ChevronDown, Sparkles, Crown } from "lucide-react";
import HeroParticles from "./HeroParticles";
import { HOTEL, HERO_STATS } from "../data/site";

const HERO_VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_maa-durga-palace/artifacts/rj8axiq8_Firefly%20make%20is%20motion%20controled%20%20887656%20%281%29.mp4";

const word = {
  hidden: { y: 40, opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.15 + i * 0.06, duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  }),
};

const headline = "Premium Wedding Palace & Comfortable Stay in Bhadrak";

export default function Hero() {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // If video is already cached/ready by the time effect runs
    if (v.readyState >= 2) setVideoReady(true);
    // Attempt autoplay (some mobile browsers need an explicit call)
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
      style={{ backgroundColor: "#0A0405" }}
    >
      {/* Cinematic background — solid dark color first, video fades in once ready (no stock image flash) */}
      <div className="absolute inset-0" style={{ backgroundColor: "#0A0405" }}>
        <video
          ref={videoRef}
          src={HERO_VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="hero-video"
          onCanPlay={() => setVideoReady(true)}
          onLoadedData={() => setVideoReady(true)}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out"
          style={{ opacity: videoReady ? 1 : 0, backgroundColor: "#0A0405" }}
        />
        {/* Warm cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A0608]/85 via-[#1A0608]/55 to-[#0A0405]/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.22),transparent_60%)]" />
      </div>

      {/* Particles */}
      <HeroParticles />

      {/* Content */}
      <div className="relative container-x min-h-screen flex flex-col justify-center pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="hero-eyebrow"
          className="inline-flex items-center gap-2 self-start rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 backdrop-blur-md px-4 py-1.5 text-xs tracking-[0.25em] uppercase text-[#E6C555]"
        >
          <Crown size={12} className="text-[#D4AF37]" />
          Kalyana Mandap · Wedding Palace · Guest House
        </motion.div>

        <h1 className="mt-7 font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-white max-w-5xl">
          {headline.split(" ").map((w, i) => (
            <motion.span
              key={`${w}-${i}`}
              custom={i}
              variants={word}
              initial="hidden"
              animate="show"
              className="inline-block mr-3"
            >
              {w === "Wedding" || w === "Palace" ? (
                <span className="gold-text italic font-medium">{w}</span>
              ) : (
                w
              )}
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          data-testid="hero-subheadline"
          className="mt-6 max-w-3xl text-base sm:text-lg text-white/85 leading-relaxed"
        >
          <span className="text-[#E6C555] font-medium">Maa Durga Palace & Guest House</span>
          {" "}— Elegant Kalyana Mandap, Wedding Venue, Reception Hall & Luxury Guest
          Accommodation in Bhadrak, Odisha.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <a
            href={HOTEL.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-venue-btn"
            className="btn-gold"
          >
            <Sparkles size={18} />
            Book Wedding Venue
          </a>
          <a
            href={HOTEL.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="btn-whatsapp"
          >
            <MessageCircle size={18} />
            WhatsApp Inquiry
          </a>
          <a href="#mandap" data-testid="hero-explore-btn" className="btn-ghost">
            Explore Mandap
            <ChevronDown size={16} />
          </a>
          <a
            href={`tel:${HOTEL.phoneE164}`}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-medium text-sm tracking-wide bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white/20 hover:-translate-y-0.5 transition-all duration-300"
          >
            <Phone size={16} />
            Call Now
          </a>
        </motion.div>

        {/* Floating stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          data-testid="hero-stats"
          className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl"
        >
          {HERO_STATS.map((s, i) => (
            <motion.div
              key={s.v}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35 + i * 0.1, duration: 0.55 }}
              whileHover={{ y: -4 }}
              className="rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-md p-4 sm:p-5"
            >
              <div className="font-display text-2xl sm:text-3xl text-[#E6C555]">{s.k}</div>
              <div className="text-[10px] sm:text-xs tracking-[0.15em] uppercase text-white/65 mt-1">
                {s.v}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Scroll hint */}
        <motion.a
          href="#mandap"
          aria-label="Scroll to mandap section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.6 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/55 hover:text-white"
        >
          <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown size={18} />
          </motion.span>
        </motion.a>
      </div>
    </section>
  );
}
